import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { HandHeart, Flame, Heart, Sparkles, Share2 } from "lucide-react";  // eslint-disable-line no-unused-vars
import { waIntentionShareUrl } from "@/lib/share";

export default function PrioritySection() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [prayingId, setPrayingId] = useState(null);

    const load = () =>
        api.get("/intentions/priority", { params: { limit: 6 } })
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]));

    useEffect(() => { load(); }, []);

    const pray = async (id) => {
        if (!user?.id) { navigate("/login"); return; }
        setPrayingId(id);
        try {
            const { data } = await api.post(`/intentions/${id}/pray`);
            setItems((prev) => prev.map((x) => (x.id === id ? { ...x, prayer_count: data.prayer_count } : x)));
        } catch { /* ignore */ } finally {
            setPrayingId(null);
        }
    };

    if (!items.length) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-10 py-16" data-testid="landing-priority">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs tracking-[0.28em] uppercase flex items-center gap-2" style={{ color: "#7F1D1D" }}>
                        <Sparkles size={14} /> {t("priority.kicker")}
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>{t("priority.h")}</h2>
                    <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>{t("priority.sub")}</p>
                </div>
            </div>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((i, idx) => (
                    <article key={i.id}
                             className={`p-card p-5 fade-up delay-${(idx % 4) + 1}`}
                             style={i.priority ? { border: "2px solid #D4AF37", boxShadow: "0 14px 40px -16px rgba(212,175,55,0.45)" } : {}}
                             data-testid={`landing-priority-${i.id}`}>
                        <div className="flex items-center justify-between gap-2">
                            {i.priority ? (
                                <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full flex items-center gap-1"
                                      style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                    <Flame size={10} /> {t("priority.pinned")}
                                </span>
                            ) : (
                                <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full"
                                      style={{ background: "#F5F0E6", color: "#7F1D1D" }}>
                                    {i.category}
                                </span>
                            )}
                            <span className="text-xs" style={{ color: "#525252" }}>{i.requester_name}</span>
                        </div>
                        <div className="font-display text-2xl mt-3" style={{ color: "#1E1B4B" }}>{i.title}</div>
                        {i.description && (
                            <div className="text-sm mt-2 line-clamp-2" style={{ color: "#525252" }}>{i.description}</div>
                        )}
                        <div className="mt-5 flex items-center justify-between">
                            <div className="text-sm flex items-center gap-1.5" style={{ color: "#7F1D1D" }} data-testid={`landing-priority-count-${i.id}`}>
                                <HandHeart size={16} style={{ color: "#D4AF37" }} />
                                <span>{i.prayer_count === 1 ? t("priority.committed_one") : t("priority.committed_many", { count: i.prayer_count || 0 })}</span>
                            </div>
                            <button onClick={() => pray(i.id)} disabled={prayingId === i.id}
                                    className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                                    data-testid={`landing-priority-pray-${i.id}`}>
                                <HandHeart size={14} /> {prayingId === i.id ? "…" : (user?.id ? t("priority.cta") : t("priority.cta_signin"))}
                            </button>
                        </div>
                        <div className="mt-3 pt-3 border-t flex" style={{ borderColor: "#F0E8D4" }}>
                            <a
                                href={waIntentionShareUrl({
                                    title: i.title,
                                    description: i.description,
                                    requesterName: i.requester_name,
                                    intentionId: i.id,
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs inline-flex items-center gap-1.5 ml-auto"
                                style={{ color: "#1E1B4B" }}
                                title={t("priority.share_wa")}
                                data-testid={`landing-priority-share-${i.id}`}
                            >
                                <Share2 size={12} style={{ color: "#25D366" }} /> {t("priority.share_wa")}
                            </a>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
